import { UseMutateAsyncFunction, useIsMutating, useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clearInterval, setInterval } from 'worker-timers';
import { InactivityPrompt, Spinner } from '../components';
import { useBrand } from '../providers';
import {
  IssuePayload,
  IssueResponse,
  RenewResponse,
  RevokeResponse,
  issueIAMToken,
  renewIAMToken,
  revokeIAMToken,
  validateToken
} from '../services/iam';

type IssueFnPayload = Omit<IssuePayload, 'baseUrl' | 'clientId' | 'brand'>;

interface AccessTokenManagementProviderProps {
  onTokenIssued?: (data: IssueResponse) => void;
  onTokenRenewed?: (data: RenewResponse) => void;
  onTokenRevoked?: () => void;
  clientId: string;
  baseUrl?: string;
  showTimer?: boolean;
  children: React.ReactNode | React.ReactNode[];
  accessTokenSessionStorageName?: string;
}

interface AccessTokenManagementProviderInterface {
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  isRevokingToken: boolean;
  isIssuingToken: boolean;
  issueToken: UseMutateAsyncFunction<IssueResponse, unknown, IssueFnPayload>;
  renewToken: () => void;
  revokeToken: () => void;
  revokeTokenAsync: UseMutateAsyncFunction;
}

export const AccessTokenManagementContext =
  createContext<AccessTokenManagementProviderInterface>(null);

const cleanSessionStorage = ({
  accessTokenSessionStorageName
}: {
  accessTokenSessionStorageName: string;
}) => {
  sessionStorage.removeItem(accessTokenSessionStorageName);
  sessionStorage.removeItem('access_token_expires_at');
  sessionStorage.removeItem('totalElapsedTime');
  sessionStorage.removeItem('lastSessionTimestamp');
};

function AccessTokenManagementProvider({
  baseUrl = 'http://localhost:3100',
  clientId,
  onTokenIssued,
  onTokenRenewed,
  onTokenRevoked,
  showTimer = false,
  children,
  accessTokenSessionStorageName = 'access_token'
}: AccessTokenManagementProviderProps) {
  const { brand } = useBrand();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(
    !!sessionStorage.getItem(accessTokenSessionStorageName) || !!searchParams.get('token')
  );
  const isRenewingToken = useIsMutating(['renew-token']);

  const timer = useRef<number>(null);

  const { mutate: callHello } = useMutation({
    mutationFn: () =>
      validateToken({
        baseUrl,
        token: sessionStorage.getItem(accessTokenSessionStorageName)
      }),
    onSuccess: () => {
      setIsAuthenticated(true);
    },
    onError: () => {
      sessionStorage.removeItem(accessTokenSessionStorageName);
      setIsAuthenticated(false);
    },
    onSettled: () => {
      setIsAuthenticating(false);
    }
  });

  const { mutate: renewToken } = useMutation<RenewResponse>({
    mutationKey: ['renew-token'],
    mutationFn: () =>
      renewIAMToken({
        baseUrl,
        clientId,
        brand,
        token: sessionStorage.getItem(accessTokenSessionStorageName)
      }),
    onSuccess: (data) => {
      sessionStorage.setItem(accessTokenSessionStorageName, data.access_token);
      sessionStorage.setItem(
        'access_token_expires_at',
        `${dayjs().add(Number(data.expires_in), 'seconds').unix()}`
      );
      onTokenRenewed?.(data);
    },
    onError: () => {
      cleanSessionStorage({ accessTokenSessionStorageName });
      setIsAuthenticated(false);
    }
  });

  const {
    mutate: revokeToken,
    mutateAsync: revokeTokenAsync,
    isLoading: isRevokingToken
  } = useMutation<RevokeResponse>({
    mutationFn: () =>
      revokeIAMToken({
        baseUrl,
        clientId,
        brand,
        token: sessionStorage.getItem(accessTokenSessionStorageName)
      }),
    onSettled: () => {
      setIsAuthenticated(false);
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
      cleanSessionStorage({ accessTokenSessionStorageName });
      onTokenRevoked?.();
    }
  });

  const { mutateAsync: issueToken, isLoading: isIssuingToken } = useMutation<
    IssueResponse,
    unknown,
    IssueFnPayload
  >({
    mutationFn: ({ token, tokenType = 'JWT', scope = [], redirectUri, nonce, state }) =>
      issueIAMToken({
        baseUrl,
        brand,
        clientId,
        token,
        tokenType,
        scope,
        redirectUri,
        nonce,
        state
      }),
    onSuccess: (data) => {
      sessionStorage.setItem(accessTokenSessionStorageName, data.access_token);
      sessionStorage.setItem(
        'access_token_expires_at',
        `${dayjs().add(Number(data.expires_in), 'seconds').unix()}`
      );
      setIsAuthenticated(true);
      onTokenIssued?.(data);
    },
    onError: () => {
      cleanSessionStorage({ accessTokenSessionStorageName });
      setIsAuthenticated(false);
    }
  });

  const onTimeout = useCallback(() => {
    revokeToken();
  }, []);

  const getAccessTokenExpiryTimestamp = () =>
    Number(sessionStorage.getItem('access_token_expires_at'));
  const getCurrentTimestamp = () => dayjs().unix();
  const isTokenRenewing = () => isRenewingToken;

  /**
   * Once authenticated we setup the timer that will check if the token is about to expire
   * (has 60 seconds or less left) and will renew it
   */
  useEffect(() => {
    if (isAuthenticated) {
      timer.current = setInterval(() => {
        if (
          // if the token is already being renewed we don't want to try renew again
          !isRenewingToken &&
          // token can be renewed 1 minute before it expires
          getAccessTokenExpiryTimestamp() - getCurrentTimestamp() <= 60
        ) {
          renewToken();
        }
      }, 1000);
    }

    return () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    };
  }, [isAuthenticated, isTokenRenewing]);

  /**
   * If there's already an access token present in sessionStorage we call
   * the /hello endpoint to check it's valid
   */
  useEffect(() => {
    if (sessionStorage.getItem(accessTokenSessionStorageName)) {
      callHello();
    }
  }, []);

  /**
   * We support passing a token and expiry time as query params to enable
   * direct access to an SPA without needing to login via normal means
   */
  useEffect(() => {
    const token = searchParams.get('token');
    const expiryTime = searchParams.get('expires');

    if (token) {
      sessionStorage.setItem(accessTokenSessionStorageName, token);

      searchParams.delete('token');

      if (expiryTime) {
        sessionStorage.setItem('access_token_expires_at', expiryTime);
        searchParams.delete('expires');
      }

      setSearchParams(searchParams);

      callHello();
    }
  }, []);

  return (
    <AccessTokenManagementContext.Provider
      value={{
        isAuthenticated,
        isAuthenticating,
        isRevokingToken,
        isIssuingToken,
        issueToken,
        renewToken,
        revokeToken,
        revokeTokenAsync
      }}
    >
      {isAuthenticating ? <Spinner isBranded isFullscreen /> : <>{children}</>}
      {isAuthenticated && (
        <InactivityPrompt
          sessionTimeoutMinutes={60}
          sessionTimeoutWarningMinutes={50}
          timeoutMinutes={15}
          promptMinutes={1}
          onTimeout={onTimeout}
          showTimer={showTimer}
        />
      )}
    </AccessTokenManagementContext.Provider>
  );
}

export default function useAccessTokenManagement() {
  return useContext(AccessTokenManagementContext);
}

export { AccessTokenManagementProvider };
